import './Tile';
import './Flyout';
import './service/ViewService';
import React from 'react';
import { Message } from 'oskari-ui';

/**
 * @class Oskari.mapframework.bundle.mydata.MyDataBundleInstance
 *
 * Main component and starting point for the personal data functionality.
 *
 * See Oskari.mapframework.bundle.mydata.MyDataBundle for bundle definition.
 */
Oskari.clazz.define('Oskari.mapframework.bundle.mydata.MyDataBundleInstance',
/**
 * @method create called automatically on construction
 * @static
 */

function () {
  this.core = null;
  this.sandbox = null;
  this.started = false;
  this.plugins = {};
  this.viewService = null;
}, {
  /**
   * @static
   * @property __name
   */
  __name: 'MyData',
  /**
   * @method getName
   * @return {String} the name for the component
   */
  'getName': function getName() {
    return this.__name;
  },
  /**
   * @method setSandbox
   * @param {Oskari.Sandbox} sandbox
   * Sets the sandbox reference to this component
   */
  setSandbox: function setSandbox(sandbox) {
    this.sandbox = sandbox;
  },
  /**
   * @method getSandbox
   * @return {Oskari.Sandbox}
   */
  getSandbox: function getSandbox() {
    return this.sandbox;
  },
  /**
   * @method getViewService
   * @return {Oskari.mapframework.bundle.mydata.service.ViewService}
   */
  getViewService: function getViewService() {
    return this.viewService;
  },
  /**
   * @method getLocalization
   * Returns JSON presentation of bundles localization data for current language.
   * If key-parameter is not given, returns the whole localization data.
   *
   * @param {String} key (optional) if given, returns the value for key
   * @return {String/Object} returns single localization string or
   *      JSON object for complete data depending on localization
   *      structure and if parameter key is given
   */
  getLocalization: function getLocalization(key) {
    if (!this._localization) {
      this._localization = Oskari.getLocalization(this.getName());
    }
    if (key) {
      return this._localization[key];
    }
    return this._localization;
  },
  /**
   * @method start
   * implements BundleInstance protocol start methdod
   */
  'start': function start() {
    var me = this;
    if (me.started) {
      return;
    }
    me.started = true;
    var conf = this.conf || {};
    this.conf = conf;
    var sandboxName = (conf ? conf.sandbox : null) || 'sandbox';
    var sandbox = Oskari.getSandbox(sandboxName),
      p;
    me.sandbox = sandbox;
    this.viewService = Oskari.clazz.create('Oskari.mapframework.bundle.mydata.service.ViewService');
    sandbox.register(me);
    for (p in me.eventHandlers) {
      if (me.eventHandlers.hasOwnProperty(p)) {
        sandbox.registerForEventByName(me, p);
      }
    }

    // Let's extend UI
    var request = Oskari.requestBuilder('userinterface.AddExtensionRequest')(this);
    sandbox.request(this, request);

    // draw ui
    me.createUi();
    this._registerForGuidedTour();
  },
  /**
   * @method init
   * implements Module protocol init method - adds tab request handler
   */
  'init': function init() {
    return null;
  },
  /**
   * @method update
   * implements BundleInstance protocol update method - does nothing atm
   */
  'update': function update() {},
  /**
   * @method onEvent
   * @param {Oskari.mapframework.event.Event} event a Oskari event object
   * Event is handled forwarded to correct #eventHandlers if found or discarded if not.
   */
  onEvent: function onEvent(event) {
    var handler = this.eventHandlers[event.getName()];
    if (!handler) {
      return;
    }
    return handler.apply(this, [event]);
  },
  /**
   * @property {Object} eventHandlers
   * @static
   */
  eventHandlers: {},
  /**
   * @method stop
   * implements BundleInstance protocol stop method
   */
  'stop': function stop() {
    var sandbox = this.sandbox(),
      p;
    for (p in this.eventHandlers) {
      if (this.eventHandlers.hasOwnProperty(p)) {
        sandbox.unregisterFromEventByName(this, p);
      }
    }
    var request = Oskari.requestBuilder('userinterface.RemoveExtensionRequest')(this);
    sandbox.request(this, request);
    this.sandbox.unregister(this);
    this.started = false;
  },
  /**
   * @method startExtension
   * implements Oskari.userinterface.Extension protocol startExtension method
   * Creates a flyout and a tile:
   * Oskari.mapframework.bundle.mydata.Flyout
   * Oskari.mapframework.bundle.mydata.Tile
   */
  startExtension: function startExtension() {
    this.plugins['Oskari.userinterface.Flyout'] = Oskari.clazz.create('Oskari.mapframework.bundle.mydata.Flyout', this);
    this.plugins['Oskari.userinterface.Tile'] = Oskari.clazz.create('Oskari.mapframework.bundle.mydata.Tile', this);
  },
  /**
   * @method stopExtension
   * implements Oskari.userinterface.Extension protocol stopExtension method
   * Clears references to flyout and tile
   */
  stopExtension: function stopExtension() {
    this.plugins['Oskari.userinterface.Flyout'] = null;
    this.plugins['Oskari.userinterface.Tile'] = null;
  },
  /**
   * @method getPlugins
   * implements Oskari.userinterface.Extension protocol getPlugins method
   * @return {Object} references to flyout and tile
   */
  getPlugins: function getPlugins() {
    return this.plugins;
  },
  /**
   * @method getTitle
   * @return {String} localized text for the title of the component
   */
  getTitle: function getTitle() {
    return this.getLocalization('title');
  },
  /**
   * @method getDescription
   * @return {String} localized text for the description of the component
   */
  getDescription: function getDescription() {
    return this.getLocalization('desc');
  },
  /**
   * @method createUi
   * (re)creates the UI for "personal data" functionality
   */
  createUi: function createUi() {
    this.plugins['Oskari.userinterface.Flyout'].createUi();
    this.plugins['Oskari.userinterface.Tile'].refresh();
  },
  /**
   * @static
   * @property __guidedTourDelegateTemplate
   * Delegate object given to guided tour bundle instance. Handles content & actions of guided tour popup.
   * Function "this" context is bound to bundle instance
   */
  __guidedTourDelegateTemplate: {
    priority: 40,
    show: function show() {
      this.sandbox.postRequestByName('userinterface.UpdateExtensionRequest', [null, 'attach', 'MyData']);
    },
    hide: function hide() {
      this.sandbox.postRequestByName('userinterface.UpdateExtensionRequest', [null, 'close', 'MyData']);
    },
    getTitle: function getTitle() {
      return this.getLocalization().guidedTour.title;
    },
    getContent: function getContent() {
      return /*#__PURE__*/React.createElement(Message, {
        bundleKey: this.getName(),
        messageKey: "guidedTour.message",
        allowHTML: true
      });
    },
    getLinks: function getLinks() {
      var me = this;
      var loc = this.getLocalization().guidedTour;
      return [{
        title: loc.openLink,
        onClick: function onClick() {
          return me.sandbox.postRequestByName('userinterface.UpdateExtensionRequest', [null, 'attach', 'MyData']);
        },
        visible: false
      }, {
        title: loc.closeLink,
        onClick: function onClick() {
          return me.sandbox.postRequestByName('userinterface.UpdateExtensionRequest', [null, 'close', 'MyData']);
        },
        visible: true
      }];
    }
  },
  /**
   * @method _registerForGuidedTour
   * Registers bundle for guided tour help functionality. Waits for guided tour load if not found
   */
  _registerForGuidedTour: function _registerForGuidedTour() {
    var me = this;
    function sendRegister() {
      var requestBuilder = Oskari.requestBuilder('Guidedtour.AddToGuidedTourRequest');
      if (requestBuilder && me.sandbox.hasHandler('Guidedtour.AddToGuidedTourRequest')) {
        var delegate = {
          bundleName: me.getName()
        };
        for (var prop in me.__guidedTourDelegateTemplate) {
          if (typeof me.__guidedTourDelegateTemplate[prop] === 'function') {
            delegate[prop] = me.__guidedTourDelegateTemplate[prop].bind(me); // bind methods to bundle instance
          } else {
            delegate[prop] = me.__guidedTourDelegateTemplate[prop]; // assign values
          }
        }
        me.sandbox.request(me, requestBuilder(delegate));
      }
    }
    function handler(msg) {
      if (msg.id === 'guidedtour') {
        sendRegister();
      }
    }
    var tourInstance = me.sandbox.findRegisteredModuleInstance('GuidedTour');
    if (tourInstance) {
      sendRegister();
    } else {
      Oskari.on('bundle.start', handler);
    }
  }
}, {
  /**
   * @property {String[]} protocol
   * @static
   */
  'protocol': ['Oskari.bundle.BundleInstance', 'Oskari.mapframework.module.Module', 'Oskari.userinterface.Extension']
});